import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { McpScope } from '@masters-club/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ttlLabel } from '../format';
import { useCreateMcpToken } from '../hooks/use-mcp';
import type { McpCreatedToken } from '../types';

/** Режим доступа токена в форме. */
type AccessMode = 'read' | 'write';

const DEFAULT_TTL_DAYS = 90;

interface CreateTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ttlOptions: number[];
  onCreated: (created: McpCreatedToken) => void;
}

/** Диалог создания персонального MCP-токена. */
export function CreateTokenDialog({ open, onOpenChange, ttlOptions, onCreated }: CreateTokenDialogProps) {
  const [name, setName] = useState('');
  const [access, setAccess] = useState<AccessMode>('read');
  const initialTtl = ttlOptions.includes(DEFAULT_TTL_DAYS) ? DEFAULT_TTL_DAYS : (ttlOptions[0] ?? DEFAULT_TTL_DAYS);
  const [ttlDays, setTtlDays] = useState(initialTtl);
  const createToken = useCreateMcpToken();

  const reset = () => {
    setName('');
    setAccess('read');
    setTtlDays(initialTtl);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    createToken.mutate(
      {
        name: trimmed,
        scopes: access === 'write' ? [McpScope.READ, McpScope.WRITE] : [McpScope.READ],
        ttlDays,
      },
      {
        onSuccess: (created) => {
          reset();
          onCreated(created);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Новый MCP-токен</DialogTitle>
            <DialogDescription>
              Токен нужен клиентам, которые не умеют входить через браузер. Права берутся из вашей роли.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="mcp-token-name">Название</Label>
            <Input
              id="mcp-token-name"
              placeholder="Например: Claude Code на рабочем ноутбуке"
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Доступ</Label>
            <RadioGroup value={access} onValueChange={(value) => setAccess(value === 'write' ? 'write' : 'read')}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="read" id="mcp-access-read" />
                <Label htmlFor="mcp-access-read" className="font-normal">
                  Только чтение
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="write" id="mcp-access-write" />
                <Label htmlFor="mcp-access-write" className="font-normal">
                  Чтение и запись
                </Label>
              </div>
            </RadioGroup>
            {access === 'write' ? (
              <Alert className="border-amber-500/50 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Запись открывает клиенту все изменяющие операции, разрешённые вашей роли: создание, правку и
                  удаление данных. Выдавайте только доверенным клиентам.
                </AlertDescription>
              </Alert>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Срок действия</Label>
            <Select value={String(ttlDays)} onValueChange={(value) => setTtlDays(Number(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ttlOptions.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {ttlLabel(days)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={!name.trim() || createToken.isPending}>
              {createToken.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Создать
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
